import AlertIcon from '@assets/icons/alert.svg';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { BadgeSize, BadgeVariant } from '@usepepper/ui/atoms/badge';
import { ItemDetailSectionBadge } from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import { OrderEntryLabel } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { useTheme } from '@/contexts/theme-context';

const ESSENTIAL_BADGE_ICON_SIZE = 10;

/**
 * Descriptor for a single status badge derived from an order entry row's
 * labels. Both the order-entry table's item-name cell and the item-detail
 * panel's status badges section consume these. Aliases the shared
 * {@link ItemDetailSectionBadge}.
 */
export type OrderEntryLabelBadge = ItemDetailSectionBadge;

/**
 * One-row label input — an order guide membership label.
 */
export interface OrderGuideLabel {
  level: string;
  orderGuideName: string;
}

/**
 * Maps an order entry row's labels into a flat array of badge descriptors,
 * in display order: availability → essential item → lead time → order guides.
 *
 * Empty / undefined inputs produce zero descriptors. Consumers should hide
 * their badge container when the returned array is empty.
 *
 * @param labels - Per-row status labels.
 * @param orderGuideLabels - Order guides the row belongs to.
 * @returns A flat array of badge descriptors.
 */
export const useOrderEntryLabels = (
  labels: OrderEntryLabel | undefined,
  orderGuideLabels: OrderGuideLabel[],
): OrderEntryLabelBadge[] => {
  const theme = useTheme();
  return useMemo(() => {
    const descriptors: OrderEntryLabelBadge[] = [];

    if (labels?.availability_label) {
      descriptors.push({
        key: 'availability',
        label: i18n.t(labels.availability_label.i18n_key),
        size: BadgeSize.SMALL,
        variant: labels.availability_label.level,
      });
    }

    if (labels?.essential_item_label) {
      descriptors.push({
        key: 'essential-item',
        label: i18n.t(labels.essential_item_label.i18n_key),
        leftElement: (
          <AlertIcon
            height={ESSENTIAL_BADGE_ICON_SIZE}
            stroke={theme.sk.color.icon.warning.primary}
            width={ESSENTIAL_BADGE_ICON_SIZE}
          />
        ),
        size: BadgeSize.XXSMALL,
        variant: labels.essential_item_label.level,
      });
    }

    if (labels?.lead_time_label) {
      descriptors.push({
        key: 'lead-time',
        label: labels.lead_time_label.displayText,
        size: BadgeSize.SMALL,
        variant: labels.lead_time_label.level,
      });
    }

    for (const label of orderGuideLabels) {
      descriptors.push({
        key: `order-guide-${label.orderGuideName}`,
        label: label.orderGuideName,
        size: BadgeSize.XSMALL,
        variant: BadgeVariant.DEFAULT,
      });
    }

    return descriptors;
  }, [labels, orderGuideLabels, theme.sk.color.icon.warning.primary]);
};
